import { FlakinessReport } from '@flakiness/flakiness-report';
import { MatchResult } from '../fql/matcher.js';
import { Ranges } from '../ranges.js';
import { WireTypes } from '../wireTypes.js';
import { CommitAnalyzer, ContributionReport } from './commitAnalyzer.js';
import { ExpandedHistogram, Histogram as H } from './histogram.js';
import { ResolvedTimeline } from './resolvedTimeline.js';
import { SpanAnalyzer } from './spanAnalyzer.js';
import { Stats as S } from './stats.js';
import { FilterContext, TestIndex } from './testIndex.js';
import { TestOutcomes as T } from './testOutcomes.js';

export type TestHistory = {
  commitId: S.CommitId,
  historyType: WireTypes.HistoryType,
  sinceTimestamp: FlakinessReport.UnixTimestampMS,
  testOutcomes: T.TestOutcomes,
  durations: H.Histogram[],
}

export class TimelineTestsReport {
  static create(timeline: ResolvedTimeline, span: SpanAnalyzer, classification: WireTypes.OutcomeClassification): TimelineTestsReport {
    const contribs = span.contributions(timeline, classification);
    const timelineOutcome = span.testOutcomes(timeline, classification);
    const timelineTests = T.flatten(timelineOutcome);
    return new TimelineTestsReport(
      timeline,
      contribs,
      timelineTests,
      timelineOutcome,
      CommitAnalyzer.computeDurationBuckets(timeline, contribs),
      CommitAnalyzer.computeTagTests(contribs),
      CommitAnalyzer.computeErrorTests(contribs),
      CommitAnalyzer.computeAnnotationTests(contribs),
      [],
    );
  }

  public readonly totalDurationMs: FlakinessReport.DurationMS;

  constructor(
    public readonly timeline: ResolvedTimeline,
    public readonly contribs: ContributionReport,
    public readonly timelineTests: T.TestRanges,
    public readonly outcomes: T.TestOutcomes,
    private _durations: H.Histogram[],

    public readonly tagTests: Map<string, T.TestRanges>,
    public readonly errorTests: Map<WireTypes.TestError, T.TestRanges>,
    public readonly annotationTests: Map<WireTypes.Annotation, T.TestRanges>,

    public readonly history: TestHistory[],
  ) {
    this.totalDurationMs = H.totalSum(this._durations) as FlakinessReport.DurationMS;
  }

  isEmpty() {
    return this.timelineTests.length === 0;
  }

  setHistory(history: TestHistory[]) {
    if (!history.length)
      return this;
    return new TimelineTestsReport(
      this.timeline,
      this.contribs,
      this.timelineTests,
      this.outcomes,
      this._durations,
      this.tagTests,
      this.errorTests,
      this.annotationTests,
      history.map(value => ({
        historyType: value.historyType,
        sinceTimestamp: value.sinceTimestamp,
        commitId: value.commitId,
        durations: H.intersectAll(value.durations, this.timelineTests),
        testOutcomes: T.intersectRanges(value.testOutcomes, this.timelineTests),
      })),
    )
  }

  regressify(unhealthyTests: T.TestRanges) {
    return new TimelineTestsReport(
      this.timeline,
      CommitAnalyzer.regressify(this.contribs, unhealthyTests),
      this.timelineTests,
      T.regressify(this.outcomes, unhealthyTests),
      this._durations,
      this.tagTests,
      this.errorTests,
      this.annotationTests,
      this.history,
    )
  }

  /**
   * Keeps only `tests`, the way a filter with that mask would. Analytics uses this to narrow
   * a report down to the tests behind an outcome transition.
   */
  intersectTests(tests: T.TestRanges): TimelineTestsReport {
    const newOutcomes = T.intersectRanges(this.outcomes, tests);
    const testsMask = T.flatten(newOutcomes);
    return new TimelineTestsReport(
      this.timeline,
      this.contribs,
      testsMask,
      newOutcomes,
      this._durations.map(d => H.intersect(d, testsMask)),
      filterEntityMap(this.tagTests, testsMask),
      filterEntityMap(this.errorTests, testsMask),
      filterEntityMap(this.annotationTests, testsMask),
      this.history.map(value => ({
        historyType: value.historyType,
        sinceTimestamp: value.sinceTimestamp,
        commitId: value.commitId,
        durations: H.intersectAll(value.durations, testsMask),
        testOutcomes: T.intersectRanges(value.testOutcomes, testsMask),
      })),
    );
  }

  applyFilter(filter: FilterContext): TimelineTestsReport {
    if (filter.fql.isEmpty())
      return this;
    const newOutcomes = T.intersectRanges(this.outcomes, filter.testsMask);
    if (filter.fql.hasStatusFilters()) {
      for (const outcome of ['expected', 'unexpected', 'skipped', 'flaked', 'regressed'] as const) {
        if (!filter.fql.acceptsOutcome(outcome))
          newOutcomes[outcome] = T.EMPTY_TESTS;
      }
    }

    let newErrorTests = this.errorTests;
    if (filter.fql.hasErrorFilters()) {
      newErrorTests = new Map();
      const includeTests: T.TestRanges[] = [];
      const excludeTests: T.TestRanges[] = [];
      if (filter.fql.matchesError(null) === MatchResult.MATCH)
        includeTests.push(T.flatten(newOutcomes));
      for (const [ error, tests ] of this.errorTests) {
        const result = filter.fql.matchesError(error);
        if (result === MatchResult.MATCH) {
          includeTests.push(tests);
          newErrorTests.set(error, tests);
        } else if (result === MatchResult.EXCLUDE) {
          excludeTests.push(tests);
        }
      }
      T.intersectRangesInplace(newOutcomes, Ranges.unionAll(includeTests));
      T.subtractRangesInplace(newOutcomes, Ranges.unionAll(excludeTests));
    }

    let newTagTests = this.tagTests;
    if (filter.fql.hasTagFilters()) {
      newTagTests = new Map();
      const includeTests: T.TestRanges[] = [];
      const excludeTests: T.TestRanges[] = [];
      if (filter.fql.matchesTag(null) === MatchResult.MATCH)
        includeTests.push(T.flatten(newOutcomes));
      for (const [ tag, tests ] of this.tagTests) {
        const result = filter.fql.matchesTag(tag);
        if (result === MatchResult.MATCH) {
          includeTests.push(tests);
          newTagTests.set(tag, tests);
        } else if (result === MatchResult.EXCLUDE) {
          excludeTests.push(tests);
        }
      }
      T.intersectRangesInplace(newOutcomes, Ranges.unionAll(includeTests));
      T.subtractRangesInplace(newOutcomes, Ranges.unionAll(excludeTests));
    }

    let newAnnotationTests = this.annotationTests;
    if (filter.fql.hasAnnotationFilters()) {
      newAnnotationTests = new Map();
      const includeTests: T.TestRanges[] = [];
      const excludeTests: T.TestRanges[] = [];
      if (filter.fql.matchesAnnotation(null) === MatchResult.MATCH)
        includeTests.push(T.flatten(newOutcomes));
      for (const [ annotation, tests ] of this.annotationTests) {
        const result = filter.fql.matchesAnnotation(annotation);
        if (result === MatchResult.MATCH) {
          includeTests.push(tests);
          newAnnotationTests.set(annotation, tests);
        } else if (result === MatchResult.EXCLUDE) {
          excludeTests.push(tests);
        }
      }
      T.intersectRangesInplace(newOutcomes, Ranges.unionAll(includeTests));
      T.subtractRangesInplace(newOutcomes, Ranges.unionAll(excludeTests));
    }

    if (filter.fql.hasLineFilters()) {
      const ln = this.lineNumbers();
      const testIndexes: S.TestIndex[] = [];
      for (let testIdx = 0 as S.TestIndex; testIdx < ln.length; ++testIdx) {
        if (filter.fql.acceptsLine(ln[testIdx]))
          testIndexes.push(testIdx);
      }
      T.intersectRangesInplace(newOutcomes, Ranges.fromSortedList(testIndexes));
    }

    if (filter.fql.hasDurationFilters()) {
      const { offset, buffer } = this.expandedDurations();
      const testIndexes: S.TestIndex[] = [];
      for (let testIdx = 0 as S.TestIndex; testIdx < buffer.length; ++testIdx) {
        if (filter.fql.acceptsDuration(buffer[testIdx]))
          testIndexes.push(testIdx + offset as S.TestIndex);
      }
      T.intersectRangesInplace(newOutcomes, Ranges.fromSortedList(testIndexes));
    }

    if (filter.fql.hasFlipRateFilters()) {
      const { offset, buffer } = this.flipRate();
      const testIndexes: S.TestIndex[] = [];
      for (let testIdx = 0 as S.TestIndex; testIdx < buffer.length; ++testIdx) {
        if (filter.fql.acceptsFlipRate(buffer[testIdx]))
          testIndexes.push(testIdx + offset as S.TestIndex);
      }
      T.intersectRangesInplace(newOutcomes, Ranges.fromSortedList(testIndexes));
    }

    if (filter.fql.hasFailRateFilters()) {
      const { offset, buffer } = this.failRate();
      const testIndexes: S.TestIndex[] = [];
      for (let testIdx = 0 as S.TestIndex; testIdx < buffer.length; ++testIdx) {
        if (filter.fql.acceptsFailRate(buffer[testIdx]))
          testIndexes.push(testIdx + offset as S.TestIndex);
      }
      T.intersectRangesInplace(newOutcomes, Ranges.fromSortedList(testIndexes));
    }

    const testsMask = T.flatten(newOutcomes);
    return new TimelineTestsReport(
      this.timeline,
      this.contribs,
      testsMask,
      newOutcomes,
      this._durations.map(d => H.intersect(d, testsMask)),
      filterEntityMap(this.tagTests, testsMask),
      filterEntityMap(newErrorTests, testsMask),
      filterEntityMap(this.annotationTests, testsMask),
      this.history.map(value => ({
        historyType: value.historyType,
        sinceTimestamp: value.sinceTimestamp,
        commitId: value.commitId,
        durations: H.intersectAll(value.durations, testsMask),
        testOutcomes: T.intersectRanges(value.testOutcomes, testsMask),
      })),
    );
  }

  testDurationMs(testIdx: S.TestIndex) {
    return H.getSum(this._durations, testIdx) as FlakinessReport.DurationMS;
  }

  private _expandedDurations?: ExpandedHistogram.ExpandedHistogram;
  expandedDurations(): ExpandedHistogram.ExpandedHistogram {
    if (!this._expandedDurations) {
      this._expandedDurations = ExpandedHistogram.sumAll(this._durations);
    }
    return this._expandedDurations;
  }

  private _lineNumbers?: Int32Array;
  lineNumbers(): Int32Array {
    if (!this._lineNumbers)
      this._lineNumbers = CommitAnalyzer.computeLineNumbers(this.contribs);
    return this._lineNumbers;
  }

  private _trend?: ExpandedHistogram.ExpandedHistogram;
  durationTrend() {
    if (!this._trend)
      this._trend = this._computeDurationTrend();
    return this._trend;
  }

  private _computeDurationTrend(): ExpandedHistogram.ExpandedHistogram {
    const domain = Ranges.domain(this.timelineTests);
    if (!domain)
      return ExpandedHistogram.EMPTY;

    const baselineHistograms: H.Histogram<number, number>[][] = [];
    let tests = this.timelineTests;
    for (let i = this.history.length - 1; i >= 0 && tests.length; --i) {
      const intermediate = this.history[i];
      const f = Ranges.intersect(tests, T.flatten(intermediate.testOutcomes));
      if (!f.length)
        continue;
      tests = Ranges.subtract(tests, f);
      baselineHistograms.push(H.intersectAll(intermediate.durations, f));
    }

    const last = this.expandedDurations();
    const baseline = ExpandedHistogram.sumAll(baselineHistograms.flat());

    const offset = domain.min;
    const trend = new Float32Array(domain.max - offset + 1);
    for (const testIdx of Ranges.iterate(this.timelineTests))
      trend[testIdx - offset] = last.buffer[testIdx - last.offset] - baseline.buffer[testIdx - baseline.offset];
    return { buffer: trend, offset };
  }

  private _flipRate?: ExpandedHistogram.ExpandedHistogram;
  flipRate() {
    if (!this._flipRate)
      this._flipRate = this._computeFlipRate();
    return this._flipRate;
  }

  private _failRate?: ExpandedHistogram.ExpandedHistogram;
  failRate() {
    if (!this._failRate)
      this._failRate = this._computeFailRate();
    return this._failRate;
  }

  private _computeFlipRate(): ExpandedHistogram.ExpandedHistogram {
    const domain = Ranges.domain(this.timelineTests);
    if (!domain)
      return ExpandedHistogram.EMPTY;

    const invocations: H.Histogram<number, number>[] = [];
    const flips: H.Histogram<number, number>[] = [];
    let lastFailed = T.EMPTY_TESTS;
    let lastPassed = T.EMPTY_TESTS;

    const computeFlips = (testOutcomes: T.TestOutcomes) => {
      const failed = Ranges.union(testOutcomes.unexpected, testOutcomes.regressed);
      const passed = Ranges.union(testOutcomes.expected, testOutcomes.flaked);
      // Count invocations
      invocations.push([
        [1, failed],
      ]);
      invocations.push([
        [1, passed],
      ]);
      invocations.push([
        [1, testOutcomes.flaked], // flaked tests are invoced +1 time
      ]);

      // Count flips
      const lastPassedToFlaked = Ranges.intersect(lastPassed, testOutcomes.flaked);
      const lastPassedToFailed = Ranges.intersect(lastPassed, failed);
      const lastFailedToPassed = Ranges.intersect(lastFailed, testOutcomes.expected);
      flips.push([
        [1, lastPassedToFlaked],
      ]);
      // those that flaked also flipped
      flips.push([
        [1, testOutcomes.flaked],
      ]);
      // lastPassed -> failed
      flips.push([
        [1, lastPassedToFailed],
      ]);
      flips.push([
        [1, lastFailedToPassed],
      ]);
      // Update status
      lastFailed = Ranges.subtract(Ranges.union(failed, lastFailed), passed);
      lastPassed = Ranges.subtract(Ranges.union(passed, lastPassed), failed);
    }

    // For pure pass/fail history, counting adjacent state changes is symmetric,
    // so left-to-right and right-to-left give the same flip count.
    // In our case, however, we also have "flaky" tests, and we encode them
    // as pass/fail, meaning a oldest-to-newest order.
    for (const hist of this.history.toReversed())
      computeFlips(hist.testOutcomes);

    const inv = ExpandedHistogram.sumAll(invocations);
    const flp = ExpandedHistogram.sumAll(flips);
    const offset = domain.min;
    const flipRate = new Float32Array(domain.max - offset + 1);
    for (const testIdx of Ranges.iterate(this.timelineTests)) {
      const invIdx = testIdx - inv.offset;
      const tInvocation = invIdx >= 0 && invIdx < inv.buffer.length ? inv.buffer[invIdx] : 0;
      const flipIdx = testIdx - flp.offset;
      const tFlips = flipIdx >= 0 && flipIdx < flp.buffer.length ? flp.buffer[flipIdx] : 0;
      if (tInvocation <= 1)
        flipRate[testIdx - offset] = 0;
      else
        flipRate[testIdx - offset] = tFlips / (tInvocation - 1);
    }
    return { buffer: flipRate, offset };
  }

  private _computeFailRate(): ExpandedHistogram.ExpandedHistogram {
    const domain = Ranges.domain(this.timelineTests);
    if (!domain)
      return ExpandedHistogram.EMPTY;

    const invocations: H.Histogram<number, number>[] = [];
    const failures: H.Histogram<number, number>[] = [];

    const accumulate = (testOutcomes: T.TestOutcomes) => {
      const failed = Ranges.union(testOutcomes.unexpected, testOutcomes.regressed);
      const passed = Ranges.union(testOutcomes.expected, testOutcomes.flaked);
      const ran = Ranges.union(failed, passed);
      invocations.push([[1, ran]]);
      failures.push([[1, failed]]);
    };

    for (const hist of this.history.toReversed())
      accumulate(hist.testOutcomes);

    const inv = ExpandedHistogram.sumAll(invocations);
    const fail = ExpandedHistogram.sumAll(failures);
    const offset = domain.min;
    const failRate = new Float32Array(domain.max - offset + 1);
    for (const testIdx of Ranges.iterate(this.timelineTests)) {
      const invIdx = testIdx - inv.offset;
      const tInvocation = invIdx >= 0 && invIdx < inv.buffer.length ? inv.buffer[invIdx] : 0;
      const failIdx = testIdx - fail.offset;
      const tFailures = failIdx >= 0 && failIdx < fail.buffer.length ? fail.buffer[failIdx] : 0;
      failRate[testIdx - offset] = tInvocation > 0 ? tFailures / tInvocation : 0;
    }
    return { buffer: failRate, offset };
  }

  wireTestStats(testIndex: TestIndex, testIdx: S.TestIndex): WireTypes.TestStats|undefined {
    const test = testIndex.tests()[testIdx];
    const contrib = this.contribs.find(run => T.includes(run.outcomes, testIdx));
    if (!contrib || !test)
      return undefined;
    const env = testIndex.environments()[contrib.jsonAttempt.environmentIdx];
    if (!env)
      return undefined;
    const fc = contrib.commitAnalyzer;
    const jsonAttempt = contrib.jsonAttempt;
    const outcome: WireTypes.Outcome = (['expected', 'unexpected', 'skipped', 'flaked', 'regressed'] as const)
      .find(outcome => Ranges.includes(this.outcomes[outcome], testIdx))!;
      
      // Fill in annotations
    const annotations: WireTypes.Annotation[] = [];
    for (const [annotation, tests] of this.annotationTests) {
      if (Ranges.includes(tests, testIdx))
        annotations.push(annotation);
    }

    const tags: string[] = [];
    for (const [tag, tests] of this.tagTests) {
      if (Ranges.includes(tests, testIdx))
        tags.push(tag);
    }

    const errors = Array.from(this.errorTests).filter(([error, tests]) => Ranges.includes(tests, testIdx)).map(([error]) => error);

    const durationMs = this.testDurationMs(testIdx);

    const baselineRun = this.history.findLast(x => T.includes(x.testOutcomes, testIdx));
    const baselineDuration = baselineRun ? H.getSum(baselineRun.durations, testIdx) : 0;

    const contribInfo = fc.contributionInfo(contrib);

    let invocations = 0, flips = 0, lastOutcome: 'expected'|'unexpected'|undefined;
    let failInvocations = 0, failures = 0;
    const computeFlips = (testOutcomes: T.TestOutcomes) => {
      let outcome = (['regressed', 'unexpected', 'expected', 'flaked', 'skipped'] as const).find(outcome => Ranges.includes(testOutcomes[outcome], testIdx));
      if (!outcome || outcome === 'skipped')
        return;
      if (outcome === 'regressed')
        outcome = 'unexpected';

      failInvocations += 1;
      if (outcome === 'unexpected')
        failures += 1;

      if (outcome === 'flaked') {
        invocations += 2;
        flips += lastOutcome === 'expected' ? 2 : 1;
        lastOutcome = 'expected';
      } else {
        invocations += 1;
        flips += lastOutcome && lastOutcome !== outcome ? 1 : 0;
        lastOutcome = outcome;
      }
    }

    // For pure pass/fail history, counting adjacent state changes is symmetric,
    // so left-to-right and right-to-left give the same flip count.
    // In our case, however, we also have "flaky" tests, and we encode them
    // as pass/fail, meaning a oldest-to-newest order.
    for (const h of this.history.toReversed())
      computeFlips(h.testOutcomes);

    return {
      test,
      category: env.category,
      testIdx,
      errors: errors.length ? errors : undefined,
      env,
      attemptIdx: jsonAttempt.attemptIdx,
      timeline: this.timeline.envGroup.serialize(),
      lineNumber: fc.lineNumbers[testIdx] as FlakinessReport.Number1Based,
      tags,
      annotations,
      flipRate: invocations > 1 ? flips / (invocations - 1) : invocations === 1 ? 0 : undefined,
      failRate: failInvocations > 0 ? failures / failInvocations : undefined,
      outcome,
      history: this.history.map(testHistory => {
        const outcome = (['regressed', 'unexpected', 'expected', 'flaked', 'skipped'] as const).find(outcome => Ranges.includes(testHistory.testOutcomes[outcome], testIdx));
        if (!outcome)
          return undefined;
        return {
          historyType: testHistory.historyType,
          sinceTimestamp: testHistory.sinceTimestamp,
          commitId: testHistory.commitId,
          outcome,
          duration: H.getSum(testHistory.durations, testIdx) as FlakinessReport.DurationMS
        };
      }).filter(x => x !== undefined),
      commitId: fc.commitId,
      runId: contrib.jsonRun.runId,
      durationMs,
      durationChangeMs: durationMs - baselineDuration as FlakinessReport.DurationMS,
      hasImage: Ranges.includes(contribInfo.testsWithImage, testIdx),
      hasTrace: Ranges.includes(contribInfo.testsWithTrace, testIdx),
      hasVideo: Ranges.includes(contribInfo.testsWithVideo, testIdx),
    };
  }
}

function filterEntityMap<Q>(map: Map<Q, T.TestRanges>, mask: T.TestRanges): Map<Q, T.TestRanges> {
  return new Map(
    Array.from(map)
      .map(([e, tests]) => [e, Ranges.intersect(tests, mask)] as const)
      .filter(([e, tests]) => !!tests.length)
  );
}
